import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { prop } from 'ramda';

import { Tab } from './components/Tab/Tab';
import { Portal } from '@interlayer/js/app/components/system/Portal';

import { TTopMenuProps } from './types';
import { tagTypeService } from '@core/js/services/tagType.service';
import { useAppSelector, usePageSelector } from '@core/js/store/hooks';
import { useShowAction } from '@interlayer/js/components/system/App/buzz/hooks/useShowAction';
import { TAG_TYPE_BUZZ } from '@interlayer/js/components/system/TagManager/buzz/constants';
import { findActiveTab } from '@interlayer/js/components/tagTypes/buzz/TopMenu/helpers/findActiveTab';
import { useIsLayoutWithoutHeader } from '@interlayer/js/app/hooks/menu/useIsLayoutWithoutHeader';
import {
    useIsLayoutRetailRegistrationWithoutHeader
} from '@interlayer/js/app/hooks/menu/useIsLayoutRetailRegistrationWithoutHeader';

import style from './TopMenu.scss';
import { TOP_MENU_PORTAL } from '@interlayer/js/components/tagTypes/buzz/TopMenu/constants';

export const TopMenu: React.FC<TTopMenuProps> = ({ config }) => {
    const {
        backgroundColor,
        iconColor,
        activeTabBackgroundColor,
        bottomBorderColor,
        menuItem = []
    } = config;

    const pageLayout = usePageSelector(prop('pageLayout'));
    const { pathname } = useLocation();
    const rootPage = useAppSelector(prop('rootPage'));
    const isWithoutHeaderLayout = useIsLayoutWithoutHeader();
    const isLayoutRetailRegistration = useIsLayoutRetailRegistrationWithoutHeader();
    const activeTabItem = useMemo(() => {
        return findActiveTab(menuItem, pathname, rootPage);
    }, [menuItem, pathname, rootPage]);
    const isShowTopMenu = useMemo(() => {
        return !isWithoutHeaderLayout
            && !isLayoutRetailRegistration
            && Boolean(pageLayout.name)
            && Boolean(menuItem.length);
    }, [isLayoutRetailRegistration, isWithoutHeaderLayout, menuItem.length, pageLayout.name]);

    useShowAction(TAG_TYPE_BUZZ.topNavigation, isShowTopMenu);

    if (!isShowTopMenu) {
        return null;
    }

    return (
        <Portal id={TOP_MENU_PORTAL}>
            <div data-testid="TopMenu_wrapper" className={style.wrapper}>
                {menuItem.map(({ icon, title, link, notification }) => (
                    <Tab
                        key={link}
                        icon={icon}
                        title={title}
                        link={link}
                        notification={notification}
                        isActive={activeTabItem?.link === link}
                        amountOfElements={menuItem.length}
                        customStyle={{
                            backgroundColor,
                            iconColor,
                            activeTabBackgroundColor,
                            bottomBorderColor
                        }}
                    />
                ))}
            </div>
        </Portal>
    );
};

tagTypeService.registerTagType({ [TAG_TYPE_BUZZ.topNavigation]: TopMenu });
